import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import jwtDecode from 'jwt-decode';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      accessToken: null,
      token: null, // Alias for legacy code compatibility
      refreshToken: null,
      username: null,
      roles: [],
      fullName: null,
      contactId: null,
      isAuthenticated: false,

      // 2FA state
      twoFactorEnabled: false,
      mfaSetupEnforced: false,
      mfaDeadline: null,
      daysRemaining: 0,

      login: (accessToken, refreshToken, responseData = {}) => {
        try {
          sessionStorage.removeItem('loginNotificationsShown');
          const decoded = jwtDecode(accessToken);
          set({
            accessToken,
            token: accessToken,
            refreshToken,
            username: decoded.sub || null,
            roles: decoded.roles || [],
            fullName: decoded.name || null,
            contactId: decoded.contactId || null,
            twoFactorEnabled: decoded.twoFactorEnabled ?? responseData.twoFactorEnabled ?? false,
            mfaSetupEnforced: decoded.mfaSetupEnforced ?? responseData.mfaSetupEnforced ?? false,
            mfaDeadline: decoded.mfaDeadline ?? responseData.mfaDeadline ?? null,
            daysRemaining: decoded.daysRemaining ?? responseData.daysRemaining ?? 0,
            isAuthenticated: true,
          });
        } catch (e) {
          console.error("Failed to decode token on login", e);
        }
      },

      logout: () => {
        try { localStorage.removeItem('pelikan-last-activity'); } catch (e) {}
        set({
          accessToken: null,
          token: null,
          refreshToken: null,
          username: null,
          roles: [],
          fullName: null,
          contactId: null,
          isAuthenticated: false,
          twoFactorEnabled: false,
          mfaSetupEnforced: false,
          mfaDeadline: null,
          daysRemaining: 0,
        });
      },

      updateAccessToken: (newAccessToken, newRefreshToken) => {
        try {
          const decoded = jwtDecode(newAccessToken);
          set({
            accessToken: newAccessToken,
            token: newAccessToken,
            refreshToken: newRefreshToken ?? get().refreshToken,
            twoFactorEnabled: decoded.twoFactorEnabled ?? get().twoFactorEnabled,
            mfaSetupEnforced: decoded.mfaSetupEnforced ?? false,
            mfaDeadline: decoded.mfaDeadline ?? get().mfaDeadline,
            daysRemaining: decoded.daysRemaining ?? get().daysRemaining,
          });
        } catch (e) {
          set({
            accessToken: newAccessToken,
            token: newAccessToken,
            refreshToken: newRefreshToken ?? get().refreshToken,
          });
        }
      },

      setTwoFactorEnabled: (enabled) => {
        set({ twoFactorEnabled: enabled, mfaSetupEnforced: false });
      },

      setMfaStatus: (statusData) => {
        if (!statusData) return;
        set({
          twoFactorEnabled: statusData.twoFactorEnabled ?? false,
          mfaSetupEnforced: statusData.mfaSetupEnforced ?? false,
          mfaDeadline: statusData.mfaDeadline ?? null,
          daysRemaining: statusData.daysRemaining ?? 0,
        });
      },

      getToken: () => get().accessToken,
      getRefreshToken: () => get().refreshToken,
    }),
    {
      name: 'pelikan-auth',
      storage: createJSONStorage(() => localStorage), // Persist tokens in localStorage for full working day session
    }
  )
);

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'pelikan-auth') {
      useAuthStore.persist.rehydrate();
    }
  });
}
